// Copyright (c) Bentley Systems
import { InputGrid, LabeledInput } from "@itwin/itwinui-react";
import { useEffect, useState } from "react";
import { EfFieldValidationTypeEnum } from "../../Helpers/enumerators";
import { validateField } from "../../Helpers/helper-functions";
import {
  sanitizeInputCurrentValue,
  skipsSanitization,
} from "./text-input-components-helper";

type LabeledInputProps = React.ComponentProps<typeof LabeledInput>;

export type BecsLabelInputProps = {
  validationType: EfFieldValidationTypeEnum;
} & LabeledInputProps;

const LabeledInputComponent = ({
  onStatusChange,
  ...props
}: BecsLabelInputProps & {
  onStatusChange?: (status: "positive" | "warning" | undefined) => void;
}) => {
  const [status, setStatus] = useState<"positive" | "warning" | undefined>(
    undefined,
  ); // Initialize with null for no status

  useEffect(() => {
    if (onStatusChange) {
      onStatusChange(status);
    }
  }, [status, onStatusChange]);

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (props.validationType === EfFieldValidationTypeEnum.IntType) {
      validateIntInput(event);
    }
    if (props.onKeyDown) {
      props.onKeyDown(event);
    }
  }

  function validateIntInput(event: React.KeyboardEvent<HTMLInputElement>) {
    if (
      !/[0-9]/.test(event.key) &&
      event.key !== "Delete" &&
      event.key !== "Backspace"
    ) {
      event.preventDefault();
    }
  }

  const getTypeString = (): string => {
    switch (props.validationType) {
      case EfFieldValidationTypeEnum.Default: {
        return "Input";
      }
      case EfFieldValidationTypeEnum.Email: {
        return "Email address";
      }
      case EfFieldValidationTypeEnum.Url: {
        return "URL";
      }
      case EfFieldValidationTypeEnum.Xml: {
        return "XML";
      }
      case EfFieldValidationTypeEnum.Json: {
        return "JSON";
      }
      case EfFieldValidationTypeEnum.XmlOrJson: {
        return "XML or JSON";
      }
      case EfFieldValidationTypeEnum.GuidType: {
        return "Unique Identifier (GUID)";
      }
      case EfFieldValidationTypeEnum.IntType: {
        return "Number";
      }
      case EfFieldValidationTypeEnum.FloatType: {
        return "Decimal Number";
      }
      //should not happen. Could be wrong eField.dataType ("System.Boolean" - > eField.Type = checkbox)
      case EfFieldValidationTypeEnum.Boolean: {
        return "True or False";
      }
      case EfFieldValidationTypeEnum.Date: {
        return "Date";
      }
      case EfFieldValidationTypeEnum.Datetime: {
        return "Date and Time";
      }
      case EfFieldValidationTypeEnum.Name200:
      case EfFieldValidationTypeEnum.Name150:
      case EfFieldValidationTypeEnum.Name100: {
        return "Name";
      }
      case EfFieldValidationTypeEnum.UniqueName: {
        return "Unique name";
      }
      case EfFieldValidationTypeEnum.MqttClientId: {
        return "Mqtt client id";
      }
      case EfFieldValidationTypeEnum.MqttKey: {
        return "Mqtt key";
      }
      case EfFieldValidationTypeEnum.Version: {
        return "Version";
      }
      case EfFieldValidationTypeEnum.SearchString: {
        return "search string. Minimum 4 characters. Must start and end with a-z, A-Z, or 0-9. Can include _, -, (), [], and spaces. Can end with * wildcard.";
      }
      case EfFieldValidationTypeEnum.SystemId: {
        return "System ID (3-200 characters, must start with a letter, end with letter/number)";
      }
      default: {
        return "";
      }
    }
  };

  return (
    <InputGrid>
      <LabeledInput
        type={props.type}
        required={props.required}
        status={status}
        message={
          status === "warning"
            ? `Please enter a valid ${  getTypeString()}`
            : undefined
        }
        displayStyle={props.displayStyle}
        label={props.label}
        placeholder={props.placeholder}
        disabled={props.disabled}
        maxLength={props.maxLength}
        defaultValue={props.defaultValue}
        svgIcon={props.svgIcon}
        iconProps={props.iconProps}
        onChange={(currentValue) => {
          if (!skipsSanitization(props.validationType)) {
            const sanitizedValue = sanitizeInputCurrentValue(
              currentValue.target.value,
            );
            currentValue.target.value = sanitizedValue;
          }

          // An optional field left empty (e.g. a PATCH-style "leave blank to keep existing value"
          // input) is valid as-is and should not be flagged with a format-validation warning.
          if (!props.required && currentValue.target.value === "") {
            setStatus(undefined);
          } else {
            // Validate input and update status directly
            const isFieldValid = validateField(
              currentValue.target.value,
              props.validationType,
            );
            setStatus(isFieldValid ? "positive" : "warning"); // Set status directly
          }

          if (props.onChange) {
            props.onChange(currentValue);
          }
        }}
        onBlur={props.onBlur}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
    </InputGrid>
  );
};

export default LabeledInputComponent;
